import { makeObservable, observable, action, computed } from 'mobx';
import { getApiErrorMessage } from '@components/common/util/util';
import { IRootStore } from '@stores/index';
import { WorkspaceRole } from './member';
import { apiAuthCaller } from '@services/apiCaller';
import {
  OrganizationTokenType,
  WorkspaceTokenType,
} from '@components/profile/organization/serviceAccessToken/types';
import { ApiPermission } from '@api';

export interface ServiceAccessToken {
  id: string;
  name: string;
  description?: string;
  type: ServiceAccessTokenType;
  organizationId: string;
  workspaceId?: string;
  workspaceRoles?: WorkspaceRole[];
}

type BaseCreateTokenRequest = {
  name: string;
  description?: string;
  organizationId: string;
};

type CreateWorkspaceTokenRequest = BaseCreateTokenRequest & {
  type: ServiceAccessTokenType.WORKSPACE;
  workspaceId: string;
} & ({ permissions: ApiPermission[] } | { workspaceRoles: WorkspaceRole[] });

type CreateOrganizationTokenRequest = BaseCreateTokenRequest & {
  type: ServiceAccessTokenType.ORGANIZATION;
};

export type CreateTokenRequest = CreateWorkspaceTokenRequest | CreateOrganizationTokenRequest;

export type ICreateTokenResp = ServiceAccessToken & { accessToken: string };

export interface IServiceAccessToken {
  loadingTokens: boolean;
  serviceAccessTokens: ServiceAccessToken[];
  organizationTokens: OrganizationTokenType[];
  workspaceTokens: WorkspaceTokenType[];
  setServiceAccessTokens: (_: ServiceAccessToken[]) => void;
  getServiceAccessTokens: () => Promise<void>;
  createServiceAccessToken: (token: CreateTokenRequest) => Promise<ICreateTokenResp | undefined>;
  deleteServiceAccessToken: (token: string) => Promise<boolean>;
  currentWorkSpaceTokens: ServiceAccessToken[];
  reset: () => void;
}

enum ServiceAccessTokenType {
  ORGANIZATION = 'organization',
  WORKSPACE = 'workspace',
}

export class ServiceAccessTokenStore implements IServiceAccessToken {
  rootStore: IRootStore;

  firstLoad = false;

  @observable loadingTokens = false;

  @observable serviceAccessTokens: ServiceAccessToken[] = [];

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  reset() {
    this.serviceAccessTokens = [];
    this.firstLoad = false;
  }

  @action.bound
  setServiceAccessTokens(tokens: ServiceAccessToken[]) {
    this.serviceAccessTokens = tokens;
  }

  @action.bound
  async getServiceAccessTokens(): Promise<void> {
    if (this.firstLoad) {
      return;
    }

    this.loadingTokens = true;

    const { messagesStore, organizationStore } = this.rootStore;
    try {
      const resp = await apiAuthCaller({ nonWorkspaceRoute: true }).get<ServiceAccessToken[]>(
        `organizations/${organizationStore.id}/service-access-tokens`,
      );
      this.setServiceAccessTokens(resp.data);
      this.firstLoad = true;
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to get Service Access Tokens'),
      );
    } finally {
      this.loadingTokens = false;
    }
  }

  @action.bound
  async createServiceAccessToken(token: CreateTokenRequest): Promise<ICreateTokenResp | undefined> {
    const { messagesStore, organizationStore } = this.rootStore;
    try {
      const resp = await apiAuthCaller({ nonWorkspaceRoute: true }).post<ICreateTokenResp>(
        `organizations/${organizationStore.id}/service-access-tokens`,
        token,
      );
      const serviceAccessToken = resp.data;
      const { accessToken, ...serviceAccessTokenResponse } = serviceAccessToken;
      this.serviceAccessTokens.push(serviceAccessTokenResponse);
      return serviceAccessToken;
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to create Service Access Token'),
      );
      return undefined;
    }
  }

  @action.bound
  async deleteServiceAccessToken(id: string): Promise<boolean> {
    const { messagesStore, organizationStore } = this.rootStore;
    const token = this.serviceAccessTokens.find((accessToken) => accessToken.id === id);
    if (!token) {
      messagesStore.showErrorMessage('Token not found');
      return false;
    }

    try {
      await apiAuthCaller({ nonWorkspaceRoute: true }).delete<void>(
        `organizations/${organizationStore.id}/service-access-tokens/${id}`,
      );

      this.serviceAccessTokens = this.serviceAccessTokens.filter(
        (accessToken) => accessToken.id !== id,
      );
      messagesStore.showSuccessMessage(`Token - ${token.name} deleted successfully`);
      return true;
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to delete Service Access Token'),
      );
      return false;
    }
  }

  @computed
  get organizationTokens() {
    return this.serviceAccessTokens.filter(
      (token): token is OrganizationTokenType => token.type === ServiceAccessTokenType.ORGANIZATION,
    );
  }

  @computed
  get workspaceTokens() {
    return this.serviceAccessTokens.filter(
      (token): token is WorkspaceTokenType => token.type === ServiceAccessTokenType.WORKSPACE,
    );
  }

  @computed
  get currentWorkSpaceTokens() {
    const { workspaceStore } = this.rootStore;

    return this.workspaceTokens.filter((token) => token.workspaceId === workspaceStore.id);
  }
}
